import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Bell, LogOut, ShieldCheck, User as UserIcon } from "lucide-react";
import { cn } from "@/design-system/lib/utils";
import { useDocumentTitle } from "@/design-system/components/ui";
import { usePortalAuth } from "../PortalAuthContext";
import { usePortalNotifications } from "./PortalNotificationsContext";
import { BrokerStatusBadge, isBrokerStatusVisible } from "./BrokerStatusBadge";

interface PortalTopBarProps {
  pageTitle?: string;
}

// Post-Rollout PR 2 (PROB-13) — portal-side top bar. Mirrors the
// internal TopBar's chrome (sticky onyx band, user menu in the right
// corner) but limits affordances to what an external user actually has
// (no global search, no messages — those are staff-only). It carries a
// notification bell with an unread-count pill (fed by the shared
// usePortalNotifications context, mounted by PortalChrome for every
// portal type). The "Phoenix Capital secure portal" trust band lives
// below, in PortalDashboardShell.
export function PortalTopBar({ pageTitle }: PortalTopBarProps) {
  const auth = usePortalAuth();
  useDocumentTitle(pageTitle);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const me = auth.me;
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount } = usePortalNotifications();
  const isBroker = me?.accountType === "Broker";
  // Journey-Stage Adaptive Dashboards (audit F-01) — the broker status
  // replaces the "Secure Portal" / page-title text in the bar while climbing
  // AND once Certified (the persistent "you're certified" affordance; the
  // badge itself decays to a compact pill 30 days after certification).
  // Archive statuses and non-broker users keep the trust branding.
  const showBrokerStatus = isBroker && isBrokerStatusVisible(me?.status);
  // Account link target — the settings tab on the user's dashboard. Brokers
  // anchor to /portal/broker so the link works from inside the deal flow too.
  const settingsPath = isBroker
    ? "/portal/broker?tab=settings"
    : `${location.pathname}?tab=settings`;
  return (
    <header
      className={cn(
        // pl-16 below md reserves the fixed mobile nav toggle's column so it
        // never covers the Secure Portal brand (the toggle ends at x=56px).
        "sticky top-0 z-20 h-16 flex items-center gap-space-3 pl-16 pr-space-3 md:px-space-5",
        "bg-onyx text-white-pure border-b border-border-dark",
      )}
      data-testid="portal-topbar"
    >
      {showBrokerStatus ? (
        <BrokerStatusBadge status={me?.status} certifiedAt={me?.brokerCertifiedAt} />
      ) : (
        <div className="flex items-center gap-space-3 min-w-0">
          <div className="flex items-center gap-space-2">
            <ShieldCheck size={18} strokeWidth={1.5} className="text-gold" aria-hidden />
            <span className="font-display font-semibold text-small uppercase tracking-label text-gold">
              Secure Portal
            </span>
          </div>
          {pageTitle && (
            <span className="hidden md:inline text-small text-white-pure/70 truncate">
              {pageTitle}
            </span>
          )}
        </div>
      )}

      <div className="ml-auto flex items-center gap-space-2">
        {/* External Collection → Notifications — bell + unread badge. Opens
            the Notifications tab on the current dashboard (preserves the
            account-type path). */}
        <button
          type="button"
          onClick={() => navigate(`${location.pathname}?tab=notifications`)}
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount} unread`
              : "Notifications"
          }
          className={cn(
            "relative inline-flex items-center justify-center h-10 w-10 rounded-md",
            "text-white-pure/80 hover:text-white-pure hover:bg-white-pure/5",
          )}
        >
          <Bell size={18} strokeWidth={1.5} aria-hidden />
          {unreadCount > 0 && (
            <span
              className="absolute -top-[2px] -right-[2px] min-w-[18px] h-[18px] px-[4px] rounded-pill bg-gold text-onyx text-[10px] font-bold flex items-center justify-center"
              aria-hidden
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
        {/* fix-broker-portal — the avatar + name button now opens the user
            menu (mirroring the staff TopBar) instead of jumping straight to
            the account page. Account & settings and Sign out both live in
            the dropdown; there's no longer a separate sign-out icon button. */}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="User menu"
            className={cn(
              "flex items-center gap-space-2 h-10 pl-space-2 pr-space-3 rounded-md",
              "text-white-pure/80 hover:text-white-pure hover:bg-white-pure/5",
            )}
          >
            <Avatar name={me?.fullName ?? "?"} avatarUrl={me?.avatarUrl} />
            <div className="hidden sm:flex flex-col items-start leading-tight text-left">
              <span className="text-small font-semibold text-white-pure truncate max-w-[10rem]">
                {me?.fullName ?? "Guest"}
              </span>
              <span className="text-label text-white-pure/60 font-display uppercase tracking-label">
                {me?.accountType ?? ""}
              </span>
            </div>
          </button>
          {menuOpen && (
            <div
              role="menu"
              className={cn(
                "absolute right-0 mt-space-2 w-56 rounded-md shadow-lg z-50",
                "border border-border-light bg-white-pure text-onyx",
              )}
            >
              <div className="px-space-3 py-space-3 border-b border-border-light">
                <div className="text-small font-semibold text-onyx truncate">{me?.fullName}</div>
                <div className="text-label text-gray-mid truncate">{me?.email}</div>
              </div>
              <Link
                to={settingsPath}
                onClick={() => setMenuOpen(false)}
                role="menuitem"
                className="w-full flex items-center gap-space-2 px-space-3 py-space-2 text-small hover:bg-white-warm"
              >
                <UserIcon size={16} strokeWidth={1.5} aria-hidden /> Account &amp; settings
              </Link>
              <div className="border-t border-border-light" />
              <button
                type="button"
                onClick={() => void auth.logout()}
                role="menuitem"
                className="w-full flex items-center gap-space-2 px-space-3 py-space-2 text-small hover:bg-white-warm"
              >
                <LogOut size={16} strokeWidth={1.5} aria-hidden /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function Avatar({ name, avatarUrl }: { name: string; avatarUrl?: string | null }) {
  // UAT FixBatch FIND-16 — render the uploaded photo when present, else the
  // initials fallback (first letters of the first + last name word).
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt=""
        className="h-8 w-8 rounded-pill object-cover"
        aria-hidden
      />
    );
  }
  const initials = name
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <span className="h-8 w-8 rounded-pill bg-gold text-onyx flex items-center justify-center text-label font-display font-bold">
      {initials || <UserIcon size={14} aria-hidden />}
    </span>
  );
}
